import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  ListItemText,
  CircularProgress,
  alpha,
  useTheme,
  Pagination,
  IconButton,
  Fade,
} from "@mui/material";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import {
  Search as SearchIcon,
  Business as BusinessIcon,
  ChevronRight as ChevronRightIcon,
  ArrowBack as ArrowBackIcon,
  PhoneIphone as PhoneIphoneIcon,
  Apple as AppleIcon,
  Telegram as TelegramIcon,
} from "@mui/icons-material";
import { useQuery } from "react-query";
import axios from "../../api/axios";
import SignInWithPhone from "./signInWithPhone";
import Register from "../registration/register";
import logo from "../../utils/images/ridePlus.png";
import googleLogo from "../../utils/images/google-g.svg";
import useAuth from "../../hooks/useAuth";
import useTenantBranding from "../../hooks/useTenantBranding";
import { BASE_URL, VERSION } from "../../utils/config";
import { persistPostAuthReturn } from "../../utils/postAuthNavigation";

const LoginFlow = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.from) {
      persistPostAuthReturn(location.state.from);
    }
  }, [location.state?.from]);
  const [searchParams] = useSearchParams();
  const consentId =
    searchParams.get("consent_id") ||
    searchParams.get("consentId") ||
    location.state?.consentId;
  const preselectedTenantId =
    searchParams.get("tenant_id") || searchParams.get("tenantId");
  const preselectedTenantName =
    searchParams.get("tenant_name") || searchParams.get("tenantName");
  const preselectedTenantLogo =
    searchParams.get("tenant_logo") || searchParams.get("tenantLogo");
  const consentGoogleRedirect =
    searchParams.get("google_redirect") ||
    searchParams.get("googleRedirect") ||
    location.state?.googleRedirect ||
    "";
  const consentAppleRedirect =
    searchParams.get("apple_redirect") ||
    searchParams.get("appleRedirect") ||
    location.state?.appleRedirect ||
    "";
  const consentTelegramRedirect =
    searchParams.get("telegram_redirect") ||
    searchParams.get("telegramRedirect") ||
    location.state?.telegramRedirect ||
    "";
  const { setCurrentTenant } = useAuth();
  const { tenantLogo, hideDefaultBranding } = useTenantBranding();

  const consentUserAction = useMemo(() => {
    const raw =
      location.state?.userAction ??
      searchParams.get("user_action") ??
      searchParams.get("userAction") ??
      "";
    return String(raw)
      .toLowerCase()
      .replace(/[-_]/g, "");
  }, [location.state?.userAction, searchParams]);

  const isConsentPhoneSignup =
    Boolean(consentId) && consentUserAction === "signup";

  const preselectedAuthMethodKeys = useMemo(() => {
    const stateEnabledMethods = Array.isArray(
      location.state?.enabledAuthMethods,
    )
      ? location.state.enabledAuthMethods
      : typeof location.state?.enabledAuthMethods === "string"
        ? [location.state.enabledAuthMethods]
        : [];

    const allKeyParams = [
      ...searchParams.getAll("auth_method_key"),
      ...searchParams.getAll("auth_method_keys"),
      ...searchParams.getAll("auth_methods"),
      ...searchParams.getAll("enabled_auth_methods"),
      ...stateEnabledMethods,
      ...searchParams.getAll("authMethodKeys"),
      ...searchParams.getAll("authMethods"),
      ...searchParams.getAll("enabledAuthMethods"),
    ].filter(Boolean);

    const splitValues = allKeyParams.flatMap((value) =>
      String(value)
        .split(",")
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean),
    );

    const unique = Array.from(new Set(splitValues));
    return unique;
  }, [searchParams, location.state]);

  const [step, setStep] = useState(1); // 1: Tenant List, 2: Auth Selection, 3: Phone Login
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [perPage] = useState(10);
  const [authMethodInfo, setAuthMethodInfo] = useState(null);
  const [isLoadingAuthInfo, setIsLoadingAuthInfo] = useState(false);
  const [availableMethods, setAvailableMethods] = useState([]);
  const [isBootstrappingPrefilledFlow, setIsBootstrappingPrefilledFlow] =
    useState(!!preselectedTenantId);


  /** When present (consent from client app), use tenant mark in headers — not the default SSO asset. */
  const consentHeaderBranding = useMemo(() => {
    if (!consentId) return null;
    const logoUrl =
      selectedTenant?.logo ||
      preselectedTenantLogo ||
      searchParams.get("tenant_logo") ||
      searchParams.get("tenantLogo") ||
      null;
    const name =
      selectedTenant?.name ||
      preselectedTenantName ||
      searchParams.get("tenant_name") ||
      searchParams.get("tenantName") ||
      "Tenant";
    return { logoUrl, name };
  }, [
    consentId,
    selectedTenant?.logo,
    selectedTenant?.name,
    preselectedTenantLogo,
    preselectedTenantName,
    searchParams,
  ]);

  const hasPreselectedTenantContext = Boolean(preselectedTenantId);
  const hasPreselectedAuthMethods = preselectedAuthMethodKeys.length > 0;

  const shouldResolveTenantMeta = hasPreselectedTenantContext && !preselectedTenantName;

  // 1. Fetch Tenant List
  const { data: tenantsData, isLoading: isLoadingTenants } = useQuery(
    ["tenants-list", page, searchQuery],
    () =>
      axios.get("/tenants/list", {
        params: {
          page,
          per_page: perPage,
          search: searchQuery,
        },
      }),
    {
      keepPreviousData: true,
      enabled: !hasPreselectedTenantContext,
    },
  );

  const tenants = tenantsData?.data?.data || [];
  const totalCount = tenantsData?.data?.meta_data?.total || 0;

  // Resolve tenant meta (name/logo) by tenant id, only when needed.
  const {
    data: tenantMetaData,
    isLoading: isLoadingTenantMeta,
    isFetching: isFetchingTenantMeta,
  } = useQuery(
    ["tenant-meta-by-id", preselectedTenantId],
    () =>
      axios.get("/tenants/list", {
        params: {
          page: 0,
          per_page: -1,
        },
      }),
    {
      enabled: Boolean(shouldResolveTenantMeta && preselectedTenantId),
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  useEffect(() => {
    if (!tenantMetaData?.data?.data || !preselectedTenantId) return;

    const resolvedTenant = tenantMetaData.data.data.find(
      (item) => String(item?.tenant?.id) === String(preselectedTenantId),
    );

    if (!resolvedTenant?.tenant) return;

    const resolvedName = resolvedTenant.tenant?.name;
    const resolvedLogo = resolvedTenant.tenant?.logo;

    setSelectedTenant((prev) => {
      if (!prev) {
        return {
          id: resolvedTenant.tenant.id,
          name: resolvedName || "Tenant",
          logo: resolvedLogo || null,
        };
      }

      // Only overwrite when we didn't already have a name from the redirect.
      const shouldOverwriteName = !preselectedTenantName && resolvedName;
      const shouldOverwriteLogo = !preselectedTenantLogo && resolvedLogo;

      return {
        ...prev,
        ...(shouldOverwriteName ? { name: resolvedName } : null),
        ...(shouldOverwriteLogo ? { logo: resolvedLogo } : null),
      };
    });
  }, [
    tenantMetaData?.data?.data,
    preselectedTenantId,
    preselectedTenantName,
    preselectedTenantLogo,
  ]);

  useEffect(() => {
    if (!hasPreselectedTenantContext) {
      setIsBootstrappingPrefilledFlow(false);
      return;
    }

    let mounted = true;

    const bootstrapPrefilledFlow = async () => {
      const tenantModel = {
        id: preselectedTenantId,
        name: preselectedTenantName || "Tenant",
        logo: preselectedTenantLogo || null,
      };

      setCurrentTenant({
        id: preselectedTenantId,
        name: preselectedTenantName || "Tenant",
        logo: preselectedTenantLogo || null,
      });
      setSelectedTenant(tenantModel);

      if (hasPreselectedAuthMethods) {
        const infoFromConsent = {
          google_redirect_url:
            preselectedAuthMethodKeys.includes("google") &&
            consentGoogleRedirect
              ? consentGoogleRedirect
              : "",
          apple_redirect_url:
            preselectedAuthMethodKeys.includes("apple") && consentAppleRedirect
              ? consentAppleRedirect
              : "",
          telegram_redirect_url:
            preselectedAuthMethodKeys.includes("telegram") &&
            consentTelegramRedirect
              ? consentTelegramRedirect
              : "",
        };

        if (mounted) {
          const methodModels = preselectedAuthMethodKeys.map((key) => ({
            key,
          }));
          setAvailableMethods(methodModels);
          setAuthMethodInfo(infoFromConsent);

          if (preselectedAuthMethodKeys.includes("phone")) {
            const hasExternalMethods = preselectedAuthMethodKeys.some(
              (key) =>
                key === "google" || key === "apple" || key === "telegram",
            );
            setStep(hasExternalMethods ? 2 : 3);
          } else {
            setStep(2);
          }

          setIsBootstrappingPrefilledFlow(false);
        }
        return;
      }

      const requestedExternalMethods = preselectedAuthMethodKeys.filter(
        (key) => key === "google" || key === "apple" || key === "telegram",
      );
      const methodsToResolve =
        requestedExternalMethods.length > 0
          ? requestedExternalMethods
          : ["google", "apple", "telegram"];

      setIsLoadingAuthInfo(true);
      try {
        const response = await axios.post("/tenants/auth-method-info", {
          tenant_id: preselectedTenantId,
          auth_method_keys: methodsToResolve,
        });

        if (mounted) {
          const info = response.data?.data || response.data;
          const discoveredExternalMethods = [
            info?.google_redirect_url ? "google" : null,
            info?.apple_redirect_url ? "apple" : null,
            info?.telegram_redirect_url ? "telegram" : null,
          ].filter(Boolean);

          setAvailableMethods([
            { key: "phone" },
            ...discoveredExternalMethods.map((key) => ({ key })),
          ]);

          if (discoveredExternalMethods.length > 0) {
            setAuthMethodInfo(info);
            setStep(2);
          } else {
            setAuthMethodInfo(null);
            setStep(3);
          }
        }
      } catch (error) {
        console.error("Error fetching preselected auth methods:", error);
        if (mounted) {
          // Fallback to phone sign-in if auth method info fetch fails.
          setStep(3);
        }
      } finally {
        if (mounted) {
          setIsLoadingAuthInfo(false);
          setIsBootstrappingPrefilledFlow(false);
        }
      }
    };

    bootstrapPrefilledFlow();

    return () => {
      mounted = false;
    };
  }, [
    hasPreselectedTenantContext,
    preselectedTenantId,
    preselectedTenantName,
    preselectedTenantLogo,
    preselectedAuthMethodKeys,
    hasPreselectedAuthMethods,
    consentGoogleRedirect,
    consentAppleRedirect,
    setCurrentTenant,
  ]);

  // 2. Fetch Auth Method Info for selected tenant
  const handleTenantSelect = async (item) => {
    const tenant = item.tenant;
    const allMethods = item.tenant_auth_methods || [];
    setAvailableMethods(allMethods);

    const externalMethods = allMethods.filter(
      (m) => m.key === "google" || m.key === "apple" || m.key === "telegram",
    );
    const hasExternalMethods = externalMethods.length > 0;

    setSelectedTenant(tenant);

    // If only phone is available, go straight to phone login (Step 3)
    if (!hasExternalMethods) {
      setCurrentTenant({ id: tenant.id, name: tenant.name, logo: tenant.logo });
      setAuthMethodInfo(null);
      setStep(3);
      return;
    }

    // Otherwise, we have external methods, so we need to fetch info and show selection
    setIsLoadingAuthInfo(true);
    const authMethodKeys = externalMethods.map((m) => m.key);

    try {
      const response = await axios.post("/tenants/auth-method-info", {
        tenant_id: tenant.id,
        auth_method_keys: authMethodKeys,
      });
      setCurrentTenant({ id: tenant.id, name: tenant.name, logo: tenant.logo });
      const info = response.data?.data || response.data;
      setAuthMethodInfo(info);
      setStep(2); // Show selection screen
    } catch (error) {
      console.error("Error fetching auth methods:", error);
      setCurrentTenant({ id: tenant.id, name: tenant.name, logo: tenant.logo });
      // In case of error, if phone is enabled, go to phone
      setStep(3);
    } finally {
      setIsLoadingAuthInfo(false);
    }
  };

  const handleBack = () => {
    if (hasPreselectedTenantContext) {
      if (step === 3) {
        const hasExternalMethods = availableMethods.some(
          (m) =>
            m.key === "google" || m.key === "apple" || m.key === "telegram",
        );
        if (hasExternalMethods) {
          setStep(2);
          return;
        }
      }
      navigate(-1);
      return;
    }

    if (step === 3) {
      // If we skipped step 2 (only phone available), go back to tenant list (Step 1)
      const hasExternalMethods = availableMethods.some(
        (m) => m.key === "google" || m.key === "apple" || m.key === "telegram",
      );
      if (!hasExternalMethods) {
        setStep(1);
        setSelectedTenant(null);
        setAuthMethodInfo(null);
      } else {
        setStep(2);
      }
    } else {
      setStep(1);
      setSelectedTenant(null);
      setAuthMethodInfo(null);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value - 1);
  };

  // No more Telegram widget/iframe logic needed — Telegram now uses a redirect URL.

  const isStep2AwaitingTenantMeta =
    step === 2 &&
    selectedTenant != null &&
    shouldResolveTenantMeta &&
    Boolean(preselectedTenantId) &&
    (isLoadingTenantMeta ||
      (isFetchingTenantMeta && !tenantMetaData?.data?.data));

  if (isStep2AwaitingTenantMeta) {
    return (
      <Container
        maxWidth="sm"
        sx={{
          p: { xs: 0, sm: 2 },
          mt: { xs: 0, sm: 4 },
          height: { xs: "100vh", sm: "auto" },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 4 },
            borderRadius: { xs: 0, sm: 4 },
            background: (theme) =>
              theme.palette.mode === "light"
                ? "linear-gradient(145deg, #f8fafc 0%, #ffffff 100%)"
                : alpha(theme.palette.background.paper, 0.8),
            border: { xs: "none", sm: "1px solid" },
            borderColor: "divider",
            height: { xs: "100vh", sm: "700px" },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress color="primary" />
        </Paper>
      </Container>
    );
  }

  if (step === 2 && selectedTenant) {
    return (
      <Container
        maxWidth="sm"
        sx={{
          p: { xs: 0, sm: 2 },
          mt: { xs: 0, sm: 4 },
          height: { xs: "100vh", sm: "auto" },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 4 },
            borderRadius: { xs: 0, sm: 4 },
            background: (theme) =>
              theme.palette.mode === "light"
                ? "linear-gradient(145deg, #f8fafc 0%, #ffffff 100%)"
                : alpha(theme.palette.background.paper, 0.8),
            border: { xs: "none", sm: "1px solid" },
            borderColor: "divider",
            height: { xs: "100vh", sm: "700px" },
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >
          <IconButton
            onClick={handleBack}
            sx={{
              position: "absolute",
              left: 16,
              top: 16,
              color: "text.secondary",
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          <Box
            sx={{
              textAlign: "center",
              mb: { xs: 2.5, sm: 4 },
              mt: { xs: 2, sm: 4 },
            }}
          >
            {consentHeaderBranding ? (
              consentHeaderBranding.logoUrl ? (
                <Box
                  component="img"
                  src={consentHeaderBranding.logoUrl}
                  alt=""
                  sx={{
                    width: { xs: 60, sm: 80 },
                    height: { xs: 60, sm: 80 },
                    borderRadius: 2.5,
                    mb: 1.5,
                    objectFit: "contain",
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    p: 0.8,
                  }}
                />
              ) : (
                <Avatar
                  sx={{
                    width: { xs: 60, sm: 80 },
                    height: { xs: 60, sm: 80 },
                    mb: 1.5,
                    borderRadius: 2.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: "primary.main",
                    fontSize: { xs: "1.5rem", sm: "2rem" },
                    fontWeight: 700,
                  }}
                >
                  {consentHeaderBranding.name?.trim()?.charAt(0) || "?"}
                </Avatar>
              )
            ) : (
              <Box
                component="img"
                src={selectedTenant.logo || logo}
                alt=""
                sx={{
                  width: { xs: 60, sm: 80 },
                  height: { xs: 60, sm: 80 },
                  borderRadius: 2.5,
                  mb: 1.5,
                  objectFit: "contain",
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  p: 0.8,
                }}
              />
            )}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                mb: 0.5,
                fontSize: { xs: "1.1rem", sm: "1.35rem" },
              }}
            >
              Welcome to {selectedTenant.name}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
            >
              {isConsentPhoneSignup
                ? "Select your preferred sign-up method"
                : "Select your preferred sign-in method"}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: 1.5, sm: 2 },
              px: { xs: 0, sm: 2 },
            }}
          >
            {/* 1. Phone Method - Always available/default */}
            <ListItemButton
              onClick={() => setStep(3)}
              sx={{
                p: { xs: 1.5, sm: 2.5 },
                borderRadius: { xs: 3, sm: 4 },
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                },
                transition: "all 0.3s ease",
              }}
            >
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: "primary.main",
                  mr: { xs: 1.5, sm: 2.5 },
                  width: { xs: 40, sm: 52 },
                  height: { xs: 40, sm: 52 },
                }}
              >
                <PhoneIphoneIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
              </Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "0.95rem", sm: "1rem" },
                  }}
                >
                  Phone Number
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                >
                  {isConsentPhoneSignup
                    ? "Create your account with phone OTP"
                    : "Secure sign-in via OTP"}
                </Typography>
              </Box>
              <ChevronRightIcon
                color="action"
                sx={{ fontSize: { xs: 20, sm: 24 } }}
              />
            </ListItemButton>

            {/* 2. Google Method */}
            {authMethodInfo?.google_redirect_url && (
              <ListItemButton
                onClick={() =>
                  (window.location.href = authMethodInfo.google_redirect_url)
                }
                sx={{
                  p: { xs: 1.5, sm: 2.5 },
                  borderRadius: { xs: 3, sm: 4 },
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  "&:hover": {
                    borderColor: "#4285F4",
                    bgcolor: alpha("#4285F4", 0.02),
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: "#fff",
                    border: "1px solid #ddd",
                    mr: { xs: 1.5, sm: 2.5 },
                    width: { xs: 40, sm: 52 },
                    height: { xs: 40, sm: 52 },
                  }}
                >
                  <Box
                    component="img"
                    src={googleLogo}
                    alt="Google"
                    sx={{ width: { xs: 20, sm: 28 } }}
                  />
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "0.95rem", sm: "1rem" },
                    }}
                  >
                    Google Account
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                  >
                    {isConsentPhoneSignup
                      ? "Sign up with Google"
                      : "Sign in with Google"}
                  </Typography>
                </Box>
                <ChevronRightIcon
                  color="action"
                  sx={{ fontSize: { xs: 20, sm: 24 } }}
                />
              </ListItemButton>
            )}

            {/* 3. Apple Method */}
            {authMethodInfo?.apple_redirect_url && (
              <ListItemButton
                onClick={() =>
                  (window.location.href = authMethodInfo.apple_redirect_url)
                }
                sx={{
                  p: { xs: 1.5, sm: 2.5 },
                  borderRadius: { xs: 3, sm: 4 },
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  "&:hover": {
                    borderColor: "#111111",
                    bgcolor: alpha("#111111", 0.02),
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: "#111111",
                    color: "#fff",
                    mr: { xs: 1.5, sm: 2.5 },
                    width: { xs: 40, sm: 52 },
                    height: { xs: 40, sm: 52 },
                  }}
                >
                  <AppleIcon sx={{ fontSize: { xs: 20, sm: 28 } }} />
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "0.95rem", sm: "1rem" },
                    }}
                  >
                    Apple Account
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                  >
                    {isConsentPhoneSignup
                      ? "Sign up with Apple"
                      : "Sign in with Apple"}
                  </Typography>
                </Box>
                <ChevronRightIcon
                  color="action"
                  sx={{ fontSize: { xs: 20, sm: 24 } }}
                />
              </ListItemButton>
            )}

            {/* 4. Telegram Method */}
            {authMethodInfo?.telegram_redirect_url && (
              <ListItemButton
                onClick={() =>
                  (window.location.href = authMethodInfo.telegram_redirect_url)
                }
                sx={{
                  p: { xs: 1.5, sm: 2.5 },
                  borderRadius: { xs: 3, sm: 4 },
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  "&:hover": {
                    borderColor: "#0088cc",
                    bgcolor: alpha("#0088cc", 0.02),
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: "#0088cc",
                    color: "#fff",
                    mr: { xs: 1.5, sm: 2.5 },
                    width: { xs: 40, sm: 52 },
                    height: { xs: 40, sm: 52 },
                  }}
                >
                  <TelegramIcon sx={{ fontSize: { xs: 20, sm: 28 } }} />
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "0.95rem", sm: "1rem" },
                    }}
                  >
                    Telegram Account
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                  >
                    {isConsentPhoneSignup
                      ? "Sign up with Telegram"
                      : "Sign in with Telegram"}
                  </Typography>
                </Box>
                <ChevronRightIcon
                  color="action"
                  sx={{ fontSize: { xs: 20, sm: 24 } }}
                />
              </ListItemButton>
            )}
          </Box>

          <Box sx={{ mt: "auto", textAlign: "center", pt: 4 }}>
            {!hideDefaultBranding && (
              <Typography variant="caption" color="text.disabled">
                Powered by SSO
              </Typography>
            )}
          </Box>
        </Paper>
      </Container>
    );
  }

  if (step === 3 && selectedTenant) {
    if (isConsentPhoneSignup) {
      return (
        <Register
          consentId={consentId}
          headerBranding={consentHeaderBranding}
        />
      );
    }
    const enabledMethodKeys = (availableMethods || [])
      .map((m) => m?.key)
      .filter(Boolean);
    return (
      <SignInWithPhone
        tenantId={selectedTenant.id}
        authMethodInfo={authMethodInfo}
        enabledAuthMethods={enabledMethodKeys}
        googleRedirect={authMethodInfo?.google_redirect_url || ""}
        appleRedirect={authMethodInfo?.apple_redirect_url || ""}
        telegramRedirect={authMethodInfo?.telegram_redirect_url || ""}
        consentId={consentId}
        consentHeaderBranding={consentId ? consentHeaderBranding : undefined}
        onBack={handleBack}
        // When we show the tenant list, hide the duplicate register CTA
        showRegisterTenant={hasPreselectedTenantContext}
      />
    );
  }

  if (isBootstrappingPrefilledFlow) {
    return (
      <Container
        maxWidth="sm"
        sx={{
          p: { xs: 0, sm: 2 },
          mt: { xs: 0, sm: 4 },
          height: { xs: "100vh", sm: "auto" },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 4 },
            borderRadius: { xs: 0, sm: 4 },
            background: (theme) =>
              theme.palette.mode === "light"
                ? "linear-gradient(145deg, #f8fafc 0%, #ffffff 100%)"
                : alpha(theme.palette.background.paper, 0.8),
            border: { xs: "none", sm: "1px solid" },
            borderColor: "divider",
            height: { xs: "100vh", sm: "700px" },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress color="primary" />
        </Paper>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="sm"
      sx={{
        p: { xs: 0, sm: 2 },
        mt: { xs: 0, sm: 4 },
        height: { xs: "100vh", sm: "auto" },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 4 },
          borderRadius: { xs: 0, sm: 4 },
          background: (theme) =>
            theme.palette.mode === "light"
              ? "linear-gradient(145deg, #f8fafc 0%, #ffffff 100%)"
              : alpha(theme.palette.background.paper, 0.8),
          border: { xs: "none", sm: "1px solid" },
          borderColor: "divider",
          height: { xs: "100vh", sm: "700px" },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            mb: 3,
            mt: { xs: 2, sm: 0 },
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src={tenantLogo || logo}
            sx={{ width: 80, mb: 1.5 }}
            alt="Logo"
          />
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              mb: 0.5,
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
            }}
          >
            Select Your Tenant
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose a workspace to continue
          </Typography>
        </Box>

        <Box sx={{ mb: 2, flexShrink: 0 }}>
          <TextField
            fullWidth
            size="medium"
            placeholder="Search tenants..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
              sx: { borderRadius: 3, bgcolor: "background.default" },
            }}
          />
        </Box>

        {isLoadingTenants || isLoadingAuthInfo ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 8,
              flexGrow: 1,
            }}
          >
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                flexGrow: 1,
                overflowY: "auto",
                px: 1,
                mx: -1,
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: (theme) => alpha(theme.palette.divider, 0.2),
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  background: (theme) => alpha(theme.palette.divider, 0.3),
                },
              }}
            >
              <List sx={{ pt: 0 }}>
                {tenants.map((item, index) => (
                  <Fade
                    in={true}
                    key={item.tenant?.id || index}
                    style={{ transitionDelay: `${index * 50}ms` }}
                  >
                    <ListItemButton
                      onClick={() => handleTenantSelect(item)}
                      sx={{
                        mb: { xs: 1, sm: 1.5 },
                        borderRadius: { xs: 2.5, sm: 3 },
                        border: "1px solid",
                        borderColor: "divider",
                        p: { xs: 1.5, sm: 2 },
                        "&:hover": {
                          bgcolor: "action.hover",
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                          borderColor: "primary.main",
                        },
                        transition: "all 0.2s",
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          src={item.tenant?.logo}
                          sx={{
                            width: { xs: 40, sm: 48 },
                            height: { xs: 40, sm: 48 },
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: "primary.main",
                            borderRadius: 1.5,
                          }}
                        >
                          <BusinessIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 600,
                              color: "text.primary",
                              fontSize: { xs: "0.9rem", sm: "1rem" },
                            }}
                          >
                            {item.tenant?.name || "Unknown Tenant"}
                          </Typography>
                        }
                      />
                      <IconButton size="small" color="primary">
                        <ChevronRightIcon
                          sx={{ fontSize: { xs: 20, sm: 24 } }}
                        />
                      </IconButton>
                    </ListItemButton>
                  </Fade>
                ))}
                {tenants.length === 0 && (
                  <Box sx={{ textAlign: "center", py: 8 }}>
                    <Typography variant="body1" color="text.secondary">
                      No tenants found
                    </Typography>
                  </Box>
                )}
              </List>
            </Box>

            {totalCount > perPage && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 1,
                  mb: { xs: 2, sm: 0 },
                  pt: 1,
                  borderTop: "1px solid",
                  borderColor: "divider",
                  flexShrink: 0,
                }}
              >
                <Pagination
                  count={Math.ceil(totalCount / perPage)}
                  page={page + 1}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                />
              </Box>
            )}

            {/* <Box
              sx={{
                mt: 2,
                textAlign: "center",
                p: { xs: 2, sm: 2.5 },
                borderRadius: 2,
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.primary.main,
                  0.03,
                )} 0%, ${alpha(theme.palette.primary.main, 0.01)} 100%)`,
                border: `1.5px dashed ${alpha(theme.palette.primary.main, 0.25)}`,
                flexShrink: 0,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 1.5,
                  fontSize: { xs: "0.875rem", sm: "0.95rem" },
                  fontWeight: 500,
                }}
              >
                Need access to another workspace?
              </Typography>
              <Button
                variant="outlined"
                onClick={() => navigate("/tenant-registration")}
                startIcon={<BusinessIcon />}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: { xs: "0.875rem", sm: "0.95rem" },
                  px: { xs: 2.5, sm: 3 },
                  py: { xs: 1, sm: 1.2 },
                  borderWidth: 1.5,
                  borderColor: alpha(theme.palette.primary.main, 0.4),
                  color: theme.palette.primary.main,
                  "&:hover": {
                    borderWidth: 1.5,
                    borderColor: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    transform: "translateY(-2px)",
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                  },
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                Register New Tenant
              </Button>
            </Box> */}
          </>
        )}
      </Paper>
    </Container>
  );
};

export default LoginFlow;
